import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PageLoader } from '../ui/PageLoader';
import { PageHeader } from '../ui/PageHeader';
import FeatureTip from '../onboarding/FeatureTip';
import PageShell from '../ui/PageShell';
import { DirectoryBrowseLayout, FilterSection } from '../ui/DirectoryBrowseLayout';
import type { Club, PaginationMeta } from '../../types/discoveryBadges';
import { useAuth } from '../../contexts/AuthContext';

const CLUB_TYPES: [string, string][] = [
  ['marque', 'Marque'],
  ['regional', 'Regional'],
  ['track', 'Track'],
  ['show', 'Show'],
  ['lifestyle', 'Lifestyle'],
  ['other', 'Other'],
];

const VERIFICATIONS: [string, string][] = [
  ['verified', 'Verified'],
  ['partner', 'Partner'],
  ['unverified', 'Unverified'],
];

type ClubFilters = {
  search?: string;
  region?: string;
  club_type?: string;
  verification?: string;
  page: number;
};

function filtersFromParams(params: URLSearchParams): ClubFilters {
  return {
    search: params.get('q') || undefined,
    region: params.get('region') || undefined,
    club_type: params.get('type') || undefined,
    verification: params.get('verification') || undefined,
    page: Math.max(1, Number(params.get('page') || 1)),
  };
}

function filtersToParams(filters: ClubFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.search) next.set('q', filters.search);
  if (filters.region) next.set('region', filters.region);
  if (filters.club_type) next.set('type', filters.club_type);
  if (filters.verification) next.set('verification', filters.verification);
  if (filters.page > 1) next.set('page', String(filters.page));
  return next;
}

const ClubsDirectoryPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [draft, setDraft] = useState<ClubFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 30,
    total_count: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, isOrganizer } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.search || '');
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.listClubs({
        search: filters.search || '',
        region: filters.region || '',
        club_type: filters.club_type || '',
        verification: filters.verification || '',
        page: filters.page,
        limit: 30,
      });
      setClubs(result.data);
      setPagination(
        result.pagination || {
          page: filters.page,
          limit: 30,
          total_count: result.data.length,
          total_pages: 1,
        }
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyDraft = () => {
    setParams(filtersToParams({ ...draft, search: searchInput.trim() || undefined, page: 1 }), {
      replace: true,
    });
    setMobileOpen(false);
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
    setDraft({ page: 1 });
  };

  const activeCount = [filters.region, filters.club_type, filters.verification].filter(Boolean).length;
  const hasActive = activeCount > 0 || Boolean(filters.search);

  return (
    <PageShell
      width="full"
      contentClassName="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full"
    >
      <PageHeader
        eyebrow="Community"
        title="Clubs"
        description="Find your people. Marque clubs and communities on Bilt."
        actions={
          isAuthenticated && isOrganizer ? (
            <Button type="button" variant="primary" onClick={() => navigate('/clubs/new')}>
              Create club
            </Button>
          ) : null
        }
      />
      <FeatureTip tipId="clubs_directory" className="mb-4">
        Join communities connected to the cars, events, and people you care about.
      </FeatureTip>
      {isAuthenticated && !isOrganizer ? (
        <p className="text-sm text-gray-500 mb-4">
          Creating clubs requires organizer access. Ask an admin for an organizer invite or capability
          grant.
        </p>
      ) : null}

      <DirectoryBrowseLayout
        filterTitle="Filters"
        activeFilterCount={activeCount}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        sidebar={
          <div className="space-y-4">
            <FilterSection id="type" label="Club type">
              <div className="flex flex-wrap gap-2">
                {CLUB_TYPES.map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={draft.club_type === val ? 'filter-chip-active' : 'filter-chip'}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        club_type: draft.club_type === val ? undefined : val,
                      })
                    }
                    aria-pressed={draft.club_type === val}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FilterSection>
            <FilterSection id="verification" label="Verification">
              <div className="flex flex-wrap gap-2">
                {VERIFICATIONS.map(([val, label]) => (
                  <button
                    key={val}
                    type="button"
                    className={draft.verification === val ? 'filter-chip-active' : 'filter-chip'}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        verification: draft.verification === val ? undefined : val,
                      })
                    }
                    aria-pressed={draft.verification === val}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FilterSection>
            <FilterSection id="region" label="Region">
              <label className="block text-sm text-gray-300">
                Region
                <input
                  className="mt-1 w-full input min-h-[40px]"
                  value={draft.region || ''}
                  onChange={(e) => setDraft({ ...draft, region: e.target.value || undefined })}
                  placeholder="City, state, or area"
                />
              </label>
            </FilterSection>
          </div>
        }
        sidebarFooter={
          <div className="flex gap-2">
            <Button type="button" variant="ghost" className="flex-1" onClick={() => setDraft({ page: 1 })}>
              Reset
            </Button>
            <Button type="button" variant="primary" className="flex-[2]" onClick={applyDraft}>
              Apply
            </Button>
          </div>
        }
      >
        <form
          className="flex flex-col sm:flex-row gap-3 mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            setParams(
              filtersToParams({ ...filters, search: searchInput.trim() || undefined, page: 1 }),
              { replace: true }
            );
          }}
          role="search"
        >
          <input
            className="input flex-1 min-h-[44px]"
            placeholder="Search clubs or regions"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search clubs"
          />
          <Button type="submit" variant="primary">
            Search
          </Button>
        </form>

        <div className="flex flex-wrap items-center gap-2 mb-4" aria-label="Active filters">
          {filters.club_type ? (
            <button
              type="button"
              className="filter-chip"
              onClick={() =>
                setParams(filtersToParams({ ...filters, club_type: undefined, page: 1 }), {
                  replace: true,
                })
              }
            >
              Type: {CLUB_TYPES.find(([v]) => v === filters.club_type)?.[1] || filters.club_type} ×
            </button>
          ) : null}
          {filters.verification ? (
            <button
              type="button"
              className="filter-chip"
              onClick={() =>
                setParams(filtersToParams({ ...filters, verification: undefined, page: 1 }), {
                  replace: true,
                })
              }
            >
              {VERIFICATIONS.find(([v]) => v === filters.verification)?.[1] || filters.verification} ×
            </button>
          ) : null}
          {filters.region ? (
            <button
              type="button"
              className="filter-chip"
              onClick={() =>
                setParams(filtersToParams({ ...filters, region: undefined, page: 1 }), {
                  replace: true,
                })
              }
            >
              Region: {filters.region} ×
            </button>
          ) : null}
          {hasActive ? (
            <button
              type="button"
              className="text-sm text-orange-400 hover:text-orange-300 cursor-pointer"
              onClick={clearAll}
            >
              Clear all
            </button>
          ) : null}
          <span className="text-sm text-gray-500 ml-auto" aria-live="polite">
            {pagination.total_count} clubs
          </span>
        </div>

        {loading ? <PageLoader message="Loading clubs" /> : null}
        {error ? (
          <EmptyState
            title="Couldn’t load clubs"
            description={error}
            action={
              <Button
                type="button"
                variant="primary"
                className="min-h-[44px]"
                onClick={() => void load()}
              >
                Try again
              </Button>
            }
          />
        ) : null}
        {!loading && !error && clubs.length === 0 ? (
          <EmptyState
            imageSrc={hasActive ? undefined : '/images/brand/empty-clubs-overlook.jpg'}
            imageAlt=""
            title={hasActive ? 'No clubs match' : 'No clubs yet'}
            description={
              hasActive
                ? 'Try a different name, type, or region.'
                : 'Be the first to list a club.'
            }
            action={
              hasActive ? (
                <Button type="button" variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              ) : isAuthenticated && isOrganizer ? (
                <Button
                  type="button"
                  variant="primary"
                  className="min-h-[44px]"
                  onClick={() => navigate('/clubs/new')}
                >
                  Create club
                </Button>
              ) : undefined
            }
          />
        ) : !loading && !error ? (
          <>
            <ul className="space-y-3">
              {clubs.map((club) => (
                <li key={club.id}>
                  <Link to={`/clubs/${club.slug}`} className="card-interactive block p-4 cursor-pointer">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        {club.logo_path ? (
                          <img
                            src={apiService.getFileUrl(club.logo_path) || club.logo_path}
                            alt=""
                            className="w-12 h-12 rounded-full object-cover border border-white/[0.08] bg-[#0a0a0a] shrink-0"
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full bg-gradient-brand-tile flex items-center justify-center text-sm font-bold text-white shrink-0"
                            aria-hidden
                          >
                            {(club.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <h2 className="heading-card">{club.name}</h2>
                          <p className="text-sm text-gray-400 mt-1">
                            {[club.region, club.club_type].filter(Boolean).join(' · ')}
                          </p>
                          {club.description ? (
                            <p className="text-sm text-gray-500 mt-2 line-clamp-2">{club.description}</p>
                          ) : null}
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full border shrink-0 ${
                          club.is_verified
                            ? 'border-orange-500/40 text-orange-300'
                            : 'border-white/[0.08] text-gray-400'
                        }`}
                      >
                        {club.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-3">{club.member_count} members</p>
                  </Link>
                </li>
              ))}
            </ul>
            {pagination.total_pages > 1 ? (
              <div className="flex justify-center gap-3 mt-8">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={pagination.page <= 1}
                  onClick={() =>
                    setParams(filtersToParams({ ...filters, page: pagination.page - 1 }), {
                      replace: true,
                    })
                  }
                >
                  Previous
                </Button>
                <span className="text-gray-400 self-center text-sm">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() =>
                    setParams(filtersToParams({ ...filters, page: pagination.page + 1 }), {
                      replace: true,
                    })
                  }
                >
                  Next
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </DirectoryBrowseLayout>
    </PageShell>
  );
};

export default ClubsDirectoryPage;
